import { Trans } from '@lingui/react';
import { NavStepper } from '../../../../components/steppers/nav-stepper/NavStepper';
import { NavStepperStep } from '../../../../components/steppers/nav-stepper/NavStepperStep';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../WarrantProgram.graphql';
import { useApolloClient } from '@apollo/client';
import { WpStepMeeting } from '../../common/components/steps/StepMeeting';
import { GqlMeetingKind } from '../../../../graphql/react-operations';
import { WpStepBoardStatement } from '../../common/components/steps/StepBoardStatement';
import { WpStepLanguages } from '../../common/components/steps/StepLanguages';
import { WpStepDates } from '../../common/components/steps/StepDates';
import { StepWiseRecordDay } from './steps/StepWiseRecordDay';
import { StepWiseShares } from './steps/StepWiseShares';
import { StepWisePrice } from './steps/StepWisePrice';
import { WpStepPaymentMethods } from '../../common/components/steps/StepPaymentMethods';
import { WpStepPayByOffset } from '../../common/components/steps/StepPayByOffset';
import { useOptionsRegistrationsSuspenseQuery } from '../../OptionsRegistration.graphql';
import { WpStepPayByInKind } from '../../common/components/steps/StepPayByInKind';
import { WpStepShareLimits } from '../../common/components/steps/StepShareLimits';
import { GqlBoardsStatementOption, GqlSeverity } from '../../../../graphql/base-types.graphql';
import { WiseStepSummary } from './steps/StepSummary';
import { WpStepAuditorStatement } from '../../common/components/steps/StepAuditorStatement';

export const WiseStepper = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const apollo = useApolloClient();

  const {
    data: { warrantProgram, wiseErrors, companySubscription },
  } = useWarrantProgramSuspenseQuery({
    variables: { companyId, warrantProgramId, getWiseErrors: true, getWpErrors: true },
  });

  const {
    data: { optionsRegistrations },
  } = useOptionsRegistrationsSuspenseQuery({ variables: { companyId, warrantProgramId: warrantProgram.id } });

  const meeting = warrantProgram.meetingConcern?.meeting;

  return (
    <NavStepper path="*" onStepSaved={() => apollo.refetchQueries({ include: [WarrantProgramDocument] })}>
      <NavStepperStep label={<Trans id="Decision" />} path="decision" index validatorStatus={wiseErrors!.meeting}>
        <WpStepMeeting
          warrantProgram={warrantProgram}
          meeting={meeting}
          hasActiveSubscription={companySubscription?.isActive ?? false}
          snrAuth={warrantProgram.snrAuthorization}
        />
      </NavStepperStep>

      {meeting && meeting.kind !== GqlMeetingKind.BoardMeeting && (
        <NavStepperStep
          label={<Trans id="Board statement" />}
          path="board-statement"
          validatorStatus={wiseErrors!.boardsStatement}
        >
          <WpStepBoardStatement warrantProgram={warrantProgram} />
        </NavStepperStep>
      )}

      {warrantProgram.boardsStatementOption === GqlBoardsStatementOption.Produce && (
        <NavStepperStep
          label={<Trans id="Auditor Statement" />}
          path="auditor-statement"
          validatorStatus={wiseErrors!.auditorsStatement}
        >
          <WpStepAuditorStatement warrantProgram={warrantProgram} />
        </NavStepperStep>
      )}

      <NavStepperStep label={<Trans id="Language" />} path="language" validatorStatus={wiseErrors!.language}>
        <WpStepLanguages warrantProgram={warrantProgram} />
      </NavStepperStep>

      <NavStepperStep label={<Trans id="Important Dates" />} path="important-dates" validatorStatus={wiseErrors!.dates}>
        <WpStepDates warrantProgram={warrantProgram} />
      </NavStepperStep>

      <NavStepperStep label={<Trans id="Record Day" />} path="record-day" validatorStatus={wiseErrors!.recordDay}>
        <StepWiseRecordDay warrantProgram={warrantProgram} />
      </NavStepperStep>

      <NavStepperStep label={<Trans id="Dilution" />} path="dilution" validatorStatus={wiseErrors!.dilution}>
        <StepWiseShares warrantProgram={warrantProgram} />
      </NavStepperStep>

      <NavStepperStep
        label={<Trans id="Price of the convertible" />}
        path="price"
        validatorStatus={wiseErrors!.dilution}
      >
        <StepWisePrice warrantProgram={warrantProgram} />
      </NavStepperStep>

      <NavStepperStep
        label={<Trans id="Payment methods" />}
        path="payment-methods"
        validatorStatus={wiseErrors!.paymentKind}
      >
        <WpStepPaymentMethods warrantProgram={warrantProgram} />
      </NavStepperStep>

      {warrantProgram.paymentKindOffset && (
        <NavStepperStep label={<Trans id="Offset" />} path="offset" validatorStatus={wiseErrors!.payByOffset}>
          <WpStepPayByOffset warrantProgram={warrantProgram} optionsRegistrations={optionsRegistrations} />
        </NavStepperStep>
      )}

      {warrantProgram.paymentKindNonCash && (
        <NavStepperStep label={<Trans id="In-Kind" />} path="in-kind" validatorStatus={wiseErrors!.nonCash}>
          <WpStepPayByInKind warrantProgram={warrantProgram} optionsRegistrations={optionsRegistrations} />
        </NavStepperStep>
      )}

      {meeting?.kind !== GqlMeetingKind.BoardMeeting && (
        <NavStepperStep
          label={<Trans id="Change of share limits" />}
          path="share-limits"
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <WpStepShareLimits warrantProgram={warrantProgram} />
        </NavStepperStep>
      )}

      <NavStepperStep
        label={<Trans id="Summary" />}
        path="summary"
        validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
      >
        <WiseStepSummary warrantProgram={warrantProgram} />
      </NavStepperStep>
    </NavStepper>
  );
};
